import firebase from "firebase/app";
import { useCallback, useEffect, useRef, useState } from "react";
import { parseMobilePhoneNumber } from "utility/phoneNumber";

import "firebase/auth";

import type { PhoneNumber } from "libphonenumber-js/mobile";

/**
 * 紐付けフローの状態。Firebase 側の状態機械をそのまま写している。
 * - input: ConfirmationResult をまだ持っていない
 * - codeSent: linkWithPhoneNumber が成功し ConfirmationResult を保持している
 * - completed: confirm() が成功し紐付けが完了した
 *
 * 送信先の番号は codeSent のときにしか存在しないため、状態に持たせて
 * 「codeSent なのに番号が無い」を型で作れないようにする。
 */
type LinkPhoneNumberState =
  | { step: "input" }
  | { step: "codeSent"; sentPhoneNumber: PhoneNumber }
  | { step: "completed" };

export type LinkPhoneNumberError =
  | "invalidPhoneNumber"
  | "invalidCode"
  | "codeExpired"
  | "alreadyInUse"
  | "alreadyLinked"
  | "tooManyRequests"
  | "recaptchaFailed"
  | "unavailable"
  | "unknown";

const toLinkPhoneNumberError = (error: unknown): LinkPhoneNumberError => {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String(error.code)
      : "";
  switch (code) {
    case "auth/invalid-phone-number":
    case "auth/missing-phone-number":
      return "invalidPhoneNumber";
    case "auth/invalid-verification-code":
    case "auth/missing-verification-code":
      return "invalidCode";
    case "auth/code-expired":
      return "codeExpired";
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
      return "alreadyInUse";
    // 他のアカウントではなく、ログイン中のアカウントに既に紐付いている場合。
    case "auth/provider-already-linked":
      return "alreadyLinked";
    case "auth/too-many-requests":
    case "auth/quota-exceeded":
      return "tooManyRequests";
    // Firebase プロジェクト側の設定が足りない場合。利用者が再試行しても直らない。
    // operation-not-allowed: Phone provider が無効
    // unauthorized-domain: アクセス元ドメインが承認済みドメインに無い
    case "auth/operation-not-allowed":
    case "auth/unauthorized-domain":
      return "unavailable";
    // reCAPTCHA のトークンが取れない・検証に失敗した場合。
    // 広告ブロッカーやトラッキング防止が www.google.com/recaptcha を遮断すると起きる。
    case "auth/invalid-app-credential":
    case "auth/captcha-check-failed":
      return "recaptchaFailed";
    default:
      return "unknown";
  }
};

/**
 * ログイン済み(メールアドレス認証)のアカウントに電話番号をSMS認証で紐付ける。
 * 電話番号単体でのログインは提供しないため、sign in ではなく linkWithPhoneNumber を使う。
 *
 * reCAPTCHA は Firebase Authentication の Phone provider が必須とするため、
 * recaptchaContainerId で指定した要素に invisible reCAPTCHA を描画する。
 * RecaptchaVerifier は1回の送信でしか使えないので、送信のたびに作り直す。
 */
export const useLinkPhoneNumber = (recaptchaContainerId: string) => {
  const [state, setState] = useState<LinkPhoneNumberState>({ step: "input" });
  const [error, setError] = useState<LinkPhoneNumberError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const verifierRef = useRef<firebase.auth.RecaptchaVerifier | null>(null);
  const confirmationResultRef = useRef<firebase.auth.ConfirmationResult | null>(
    null
  );

  const clearVerifier = useCallback(() => {
    try {
      verifierRef.current?.clear();
    } catch (e) {
      // 破棄済みの verifier に clear() すると throw することがある。
      // 後片付けなので送信処理を止めない。
      console.error(e);
    }
    verifierRef.current = null;
    // clear() は grecaptcha の widget を reset するがコンテナの DOM は残す。
    // 残ったまま同じ要素へ再描画すると
    // "reCAPTCHA has already been rendered in this element" で失敗するため、
    // ここで空にする。この要素は React が子を持たないので直接触ってよい。
    const container = document.getElementById(recaptchaContainerId);
    if (container !== null) {
      container.innerHTML = "";
    }
  }, [recaptchaContainerId]);

  useEffect(() => {
    return clearVerifier;
  }, [clearVerifier]);

  const requestVerificationCode = useCallback(
    async (inputPhoneNumber: string) => {
      setError(null);

      const phoneNumber = parseMobilePhoneNumber(inputPhoneNumber);
      if (phoneNumber === null) {
        setError("invalidPhoneNumber");
        return;
      }

      const { currentUser } = firebase.auth();
      if (currentUser === null) {
        setError("unknown");
        return;
      }

      setIsSubmitting(true);
      try {
        clearVerifier();
        const verifier = new firebase.auth.RecaptchaVerifier(
          recaptchaContainerId,
          { size: "invisible" }
        );
        verifierRef.current = verifier;

        confirmationResultRef.current = await currentUser.linkWithPhoneNumber(
          phoneNumber.number,
          verifier
        );
        setState({ step: "codeSent", sentPhoneNumber: phoneNumber });
      } catch (e) {
        console.error(e);
        clearVerifier();
        setError(toLinkPhoneNumberError(e));
      } finally {
        setIsSubmitting(false);
      }
    },
    [clearVerifier, recaptchaContainerId]
  );

  const confirmVerificationCode = useCallback(
    async (code: string) => {
      setError(null);

      const confirmationResult = confirmationResultRef.current;
      if (confirmationResult === null) {
        setError("unknown");
        return;
      }

      setIsSubmitting(true);
      try {
        await confirmationResult.confirm(code);
        clearVerifier();
        confirmationResultRef.current = null;
        setState({ step: "completed" });
      } catch (e) {
        console.error(e);
        setError(toLinkPhoneNumberError(e));
      } finally {
        setIsSubmitting(false);
      }
    },
    [clearVerifier]
  );

  const backToInput = useCallback(() => {
    clearVerifier();
    confirmationResultRef.current = null;
    setError(null);
    setState({ step: "input" });
  }, [clearVerifier]);

  return {
    state,
    error,
    isSubmitting,
    requestVerificationCode,
    confirmVerificationCode,
    backToInput,
  };
};
